/**
 * Cart drawer - open/close, focus management, and all cart-mutation
 * handling (add/quantity-change/remove), consolidated here instead of
 * scattered across assets/app.js.liquid's ~1000 lines. Server-rendered
 * cart HTML (via the `sections=cart-drawer-items,cart-upsell` parameter on
 * cart/add.js, cart/update.js, cart/change.js) replaces the old client-side
 * refreshCart() string-concatenation function entirely - see
 * snippets/cart-drawer-items.liquid for the Liquid this swaps in.
 *
 * The one piece of business logic that genuinely has to stay JS: BOGO /
 * quantity-limit-left detection in updateQuantity() below, since it
 * compares what the shopper asked for against what the cart API actually
 * granted - Liquid alone can't express that comparison.
 */
(function () {
  'use strict';

  var drawer = document.querySelector('[data-cart-drawer]');
  if (!drawer) return;

  var itemsMount = drawer.querySelector('[data-cart-drawer-items]');
  var upsellMount = drawer.querySelector('.meta_upsell_product');
  var totalMount = drawer.querySelector('[data-cart-drawer-total]');
  var footer = drawer.querySelector('[data-cart-drawer-footer]');
  var lastFocused = null;
  var SECTIONS = 'cart-drawer-items,cart-upsell,cart-drawer-total';

  // ---------------------------------------------------------------------
  // Open / close, focus trap, Escape-to-close, focus-return
  // ---------------------------------------------------------------------

  function getFocusable() {
    return Array.prototype.slice.call(
      drawer.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]), textarea:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])')
    );
  }

  function trapFocus(e) {
    var focusable = getFocusable();
    if (!focusable.length) return;
    var first = focusable[0];
    var last = focusable[focusable.length - 1];
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  }

  function onKeydown(e) {
    if (e.key === 'Escape' || e.key === 'Esc') {
      closeDrawer();
    } else if (e.key === 'Tab') {
      trapFocus(e);
    }
  }

  // Scroll lock + backdrop are handled directly here rather than via the
  // sitewide window.siteOverlay (mobile-search, etc.) - its close() is
  // silently gated behind a mobile-only media query (see the comment in
  // sections/cart-drawer.liquid), so it never closes on desktop.
  function openDrawer() {
    if (drawer.hasAttribute('data-open')) return;
    lastFocused = document.activeElement;
    drawer.setAttribute('data-open', '');
    drawer.setAttribute('aria-hidden', 'false');
    document.body.classList.add('scroll-locked');
    document.addEventListener('keydown', onKeydown);
    var focusable = getFocusable();
    if (focusable.length) focusable[0].focus();
  }

  function closeDrawer() {
    if (!drawer.hasAttribute('data-open')) return;
    drawer.removeAttribute('data-open');
    drawer.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('scroll-locked');
    document.removeEventListener('keydown', onKeydown);
    if (lastFocused && typeof lastFocused.focus === 'function') lastFocused.focus();
  }

  document.addEventListener('click', function (e) {
    if (e.target.closest('[data-cart-drawer-open]')) {
      e.preventDefault();
      openDrawer();
    } else if (e.target.closest('[data-cart-drawer-close]')) {
      e.preventDefault();
      closeDrawer();
    }
  });

  // ---------------------------------------------------------------------
  // Applying a cart-mutation response (server-rendered sections + cart JSON)
  // ---------------------------------------------------------------------

  // The sections= API wraps each section's HTML in a
  // #shopify-section-<handle> .shopify-section div; unwrap it so the
  // mount points' contents are identical regardless of which endpoint
  // produced them (matches the same unwrap already proven out for the
  // upsell strip in the previous cart-upsell.liquid work).
  function unwrapSection(html) {
    if (!html) return html;
    var wrapper = document.createElement('div');
    wrapper.innerHTML = html;
    var shopifySection = wrapper.querySelector('.shopify-section');
    return shopifySection ? shopifySection.innerHTML : html;
  }

  function applySections(sections) {
    if (!sections) return;
    if (sections['cart-drawer-items'] && itemsMount) {
      itemsMount.innerHTML = unwrapSection(sections['cart-drawer-items']);
    }
    if (sections['cart-upsell'] && upsellMount) {
      upsellMount.innerHTML = unwrapSection(sections['cart-upsell']);
    }
    if (sections['cart-drawer-total'] && totalMount) {
      totalMount.innerHTML = unwrapSection(sections['cart-drawer-total']);
    }
  }

  function refreshCartCount(cart) {
    document.querySelectorAll('.cart_count').forEach(function (el) {
      el.textContent = cart.item_count;
    });

    var countLabel = drawer.querySelector('[data-cart-drawer-count]');
    if (countLabel && window.PXUTheme && window.PXUTheme.translation) {
      var noun = cart.item_count === 1
        ? window.PXUTheme.translation.cart_item_singular
        : window.PXUTheme.translation.cart_item_plural;
      countLabel.textContent = '(' + cart.item_count + ' ' + noun + ')';
    }
  }

  function afterCartUpdate(cart, sections) {
    applySections(sections);
    refreshCartCount(cart);
    if (footer) {
      if (cart.item_count === 0) {
        footer.setAttribute('hidden', '');
      } else {
        footer.removeAttribute('hidden');
      }
    }
    if (window.PXUTheme && window.PXUTheme.currency && window.PXUTheme.currency.show_multiple_currencies && window.currencyConverter) {
      window.currencyConverter.convertCurrencies();
    }
  }

  // ---------------------------------------------------------------------
  // Quantity change / remove - ported from the old refreshCart-era
  // updateCartItemQuantity(), including the BOGO / quantity-limit-left
  // detection: when the shopper's requested quantity exceeds what the
  // cart API actually granted, distinguish "a new line item appeared"
  // (e.g. an auto-added BOGO free-gift line - needs a full re-render)
  // from "this line's quantity was just capped" (needs an inline warning
  // + the input rolled back to what was actually granted).
  // ---------------------------------------------------------------------

  function updateQuantity(cartItem) {
    fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ quantity: cartItem.quantity, line: cartItem.lineID, sections: SECTIONS })
    })
      .then(function (res) { return res.json(); })
      .then(function (data) {
        var cartItemsArray = data.items;
        var lineIDIndex = cartItem.lineID - 1;
        var totalCartItems = itemsMount.querySelectorAll('[data-variant-id]').length;
        var quantityInputs = itemsMount.querySelectorAll('[data-variant-id="' + cartItem.variantID + '"] input[data-cart-drawer-quantity]');
        var apiLineItemQuantity = typeof cartItemsArray[lineIDIndex] !== 'undefined' ? cartItemsArray[lineIDIndex].quantity : 0;
        var initialQuantityTotal = 0;
        var apiQuantityTotal = 0;

        if (quantityInputs.length > 1) {
          quantityInputs.forEach(function (input) {
            initialQuantityTotal += parseInt(input.value, 10) || 0;
          });
          cartItemsArray.forEach(function (item) {
            if (item.variant_id === cartItem.variantID) apiQuantityTotal += item.quantity;
          });
        } else {
          initialQuantityTotal = parseInt(cartItem.quantity, 10) || 0;
          apiQuantityTotal = apiLineItemQuantity;
        }

        if (initialQuantityTotal > 0 && initialQuantityTotal > apiQuantityTotal) {
          var itemsLeftText = apiQuantityTotal === 1
            ? window.PXUTheme.translation.one_item_left
            : window.PXUTheme.translation.items_left_text;
          var existingWarning = itemsMount.querySelector('.warning--quantity');
          if (existingWarning) existingWarning.remove();

          if (totalCartItems < cartItemsArray.length) {
            // A new line item appeared (e.g. an auto-added BOGO line) -
            // the DOM can't be patched in place, needs a full re-render.
            afterCartUpdate(data, data.sections);
          } else {
            var warning = document.createElement('p');
            warning.className = 'warning warning--quantity animated bounceIn';
            warning.textContent = apiQuantityTotal + ' ' + itemsLeftText;
            var lineEl = itemsMount.querySelector('[data-line-id="' + cartItem.lineID + '"] input[data-cart-drawer-quantity]');
            if (lineEl) {
              lineEl.parentElement.after(warning);
              lineEl.value = apiLineItemQuantity;
            }
          }
        } else {
          afterCartUpdate(data, data.sections);
        }
      });
  }

  // Quantity input uses data-cart-drawer-quantity (not the site-wide
  // data-cart-quantity-input the full /cart page uses) specifically so it
  // does NOT match assets/app.js.liquid's body-delegated
  // `[data-cart-quantity-input]` change handler - both would otherwise
  // fire for the same input and double-POST /cart/change.js. The +/-
  // stepper buttons themselves keep the shared `.js-change-quantity`
  // class unchanged: assets/sections.js.liquid's initializeQuantityBox()
  // is already body-delegated, finds the sibling <input> generically
  // (doesn't care about its other attributes) and fires the change - no
  // need to duplicate that logic here, just listen for the event it
  // fires. That listener has to be jQuery, not addEventListener: jQuery's
  // $el.trigger('change') (what initializeQuantityBox() uses) only
  // invokes jQuery-registered handlers, it does not dispatch a real
  // native DOM event addEventListener('change', ...) would ever see -
  // confirmed live, this is why typing/pasting directly into the input
  // still works (real native change) but the +/- buttons didn't update
  // the cart at all until this was switched to $(drawer).on('change', ...).
  $(drawer).on('change', '[data-cart-drawer-quantity]', function () {
    var $input = $(this);
    var $li = $input.closest('[data-cart-item]');
    if (!$li.length) return;
    updateQuantity({
      lineID: parseInt($li.attr('data-line-id'), 10),
      variantID: parseInt($li.attr('data-variant-id'), 10),
      quantity: $input.val()
    });
  });

  drawer.addEventListener('click', function (e) {
    var removeBtn = e.target.closest('[data-cart-drawer-remove]');
    if (!removeBtn) return;
    e.preventDefault();
    var li = removeBtn.closest('[data-cart-item]');
    if (!li) return;
    li.classList.add('animated', 'fadeOutLeft');
    updateQuantity({
      lineID: parseInt(li.getAttribute('data-line-id'), 10),
      variantID: parseInt(li.getAttribute('data-variant-id'), 10),
      quantity: 0
    });
  });

  // ---------------------------------------------------------------------
  // Upsell "ADD" button
  // ---------------------------------------------------------------------

  document.addEventListener('click', function (e) {
    var addBtn = e.target.closest('.single_upsell_product .add_product_data');
    if (!addBtn) return;
    var variantId = parseInt(addBtn.getAttribute('data_variant_id'), 10);
    fetch(window.Shopify.routes.root + 'cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }], sections: SECTIONS })
    })
      .then(function (res) { return res.json(); })
      .then(function (addResponse) {
        return fetch('/cart.js').then(function (res) { return res.json(); }).then(function (cart) {
          afterCartUpdate(cart, addResponse.sections);
        });
      })
      .catch(function (error) {
        console.error('Error:', error);
      });
  });

  // Exposed for assets/app.js.liquid's PDP add-to-cart handler.
  window.CartDrawer = {
    open: openDrawer,
    close: closeDrawer,
    refresh: afterCartUpdate,
    sectionsParam: SECTIONS
  };
})();
